import { useEffect, useState, type ChangeEvent, type KeyboardEvent } from 'react'

import { cn } from '@/common/lib/cn/cn'
import { Button } from '@/common/ui/Form/Button'
import { Input } from '@/common/ui/Form/Input'
import { InputSearch } from '@/common/ui/Form/InputSearch'
import { Icon } from '@/common/ui/Icon'

import { useBrands, useCreateBrand, useDeleteBrand, useRenameBrand, type ProductBrand } from '../model'

type BrandManagerProps = {
    selectedBrandId: string | null
    onSelect: (brand: ProductBrand | null) => void
    className?: string
}

const SEARCH_DEBOUNCE_MS = 300

export const BrandManager = ({ selectedBrandId, onSelect, className }: BrandManagerProps) => {
    const [searchDraft, setSearchDraft] = useState('')
    const [search, setSearch] = useState('')
    const [newBrandName, setNewBrandName] = useState('')
    const [editingBrandId, setEditingBrandId] = useState<string | null>(null)
    const [editingName, setEditingName] = useState('')
    const [deletingBrandId, setDeletingBrandId] = useState<string | null>(null)

    useEffect(() => {
        const id = window.setTimeout(() => setSearch(searchDraft.trim()), SEARCH_DEBOUNCE_MS)
        return () => window.clearTimeout(id)
    }, [searchDraft])

    const { data: brandsPage, isLoading } = useBrands(search || undefined)
    const { mutate: createBrand, isPending: isCreating } = useCreateBrand()
    const { mutate: renameBrand, isPending: isRenaming } = useRenameBrand()
    const { mutate: deleteBrand, isPending: isDeleting } = useDeleteBrand()

    const brands = brandsPage?.items ?? []

    const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
        setSearchDraft(e.target.value)
    }

    const handleCreateBrand = () => {
        const name = newBrandName.trim()
        if (!name || isCreating) return
        createBrand(name, {
            onSuccess: brand => {
                setNewBrandName('')
                onSelect(brand)
            },
        })
    }

    const handleNewBrandKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key !== 'Enter') return
        e.preventDefault()
        handleCreateBrand()
    }

    const handleStartRename = (brand: ProductBrand) => {
        setDeletingBrandId(null)
        setEditingBrandId(brand.id)
        setEditingName(brand.name)
    }

    const handleCancelRename = () => {
        setEditingBrandId(null)
        setEditingName('')
    }

    const handleSaveRename = () => {
        const name = editingName.trim()
        if (!editingBrandId || !name || isRenaming) return
        renameBrand(
            { brandId: editingBrandId, name },
            {
                onSuccess: handleCancelRename,
            }
        )
    }

    const handleRenameKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            handleSaveRename()
            return
        }
        if (e.key === 'Escape') {
            e.preventDefault()
            handleCancelRename()
        }
    }

    const handleConfirmDelete = (brand: ProductBrand) => {
        if (isDeleting) return
        deleteBrand(brand.id, {
            onSuccess: () => {
                setDeletingBrandId(null)
                if (selectedBrandId === brand.id) {
                    onSelect(null)
                }
            },
        })
    }

    const renderBrandRow = (brand: ProductBrand) => {
        const isSelected = brand.id === selectedBrandId
        const isEditing = brand.id === editingBrandId
        const isConfirmingDelete = brand.id === deletingBrandId

        if (isEditing) {
            return (
                <li key={brand.id} className='flex items-center gap-2 rounded-[8px] bg-base-200/40 px-3 py-2'>
                    <Input
                        value={editingName}
                        onChange={e => setEditingName(e.target.value)}
                        onKeyDown={handleRenameKeyDown}
                        size='md'
                        emphasis='neutral'
                        className='w-full'
                        autoFocus
                        aria-label='Новое название бренда'
                    />
                    <Button
                        type='button'
                        size='sm'
                        emphasis='accent'
                        onClick={handleSaveRename}
                        disabled={isRenaming || !editingName.trim()}
                        loading={isRenaming}
                        aria-label='Сохранить название бренда'
                    >
                        <Icon name='check' size='sm' />
                    </Button>
                    <Button
                        type='button'
                        size='sm'
                        emphasis='secondary'
                        variant='ghost'
                        onClick={handleCancelRename}
                        aria-label='Отменить переименование'
                    >
                        <Icon name='cross' size='sm' />
                    </Button>
                </li>
            )
        }

        return (
            <li
                key={brand.id}
                className={cn(
                    'group flex items-center gap-2 rounded-[8px] px-3 py-2 transition-colors',
                    isSelected ? 'bg-[rgba(255,255,255,0.08)]' : 'hover:bg-[rgba(255,255,255,0.04)]'
                )}
            >
                <button
                    type='button'
                    tabIndex={0}
                    className='flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-left'
                    onClick={() => onSelect(brand)}
                    onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            onSelect(brand)
                        }
                    }}
                    aria-label={`Выбрать бренд ${brand.name}`}
                    aria-pressed={isSelected}
                >
                    <span
                        className={cn(
                            'flex size-4 shrink-0 items-center justify-center rounded-full border',
                            isSelected
                                ? 'border-accent text-accent'
                                : 'border-[var(--pixorion-gray-2)] text-transparent'
                        )}
                        aria-hidden
                    >
                        <span className={cn('size-2 rounded-full', isSelected && 'bg-accent')} />
                    </span>
                    <span className='truncate text-sm text-white'>{brand.name}</span>
                </button>

                {isConfirmingDelete ? (
                    <div className='flex shrink-0 items-center gap-2'>
                        <span className='text-xs text-[var(--pixorion-gray-2)]'>Удалить?</span>
                        <Button
                            type='button'
                            size='sm'
                            emphasis='accent'
                            onClick={() => handleConfirmDelete(brand)}
                            disabled={isDeleting}
                            loading={isDeleting}
                            aria-label={`Подтвердить удаление бренда ${brand.name}`}
                        >
                            Да
                        </Button>
                        <Button
                            type='button'
                            size='sm'
                            emphasis='secondary'
                            variant='ghost'
                            onClick={() => setDeletingBrandId(null)}
                            aria-label='Отменить удаление'
                        >
                            Нет
                        </Button>
                    </div>
                ) : (
                    <div className='flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100'>
                        <Button
                            type='button'
                            size='sm'
                            emphasis='secondary'
                            variant='ghost'
                            onClick={() => handleStartRename(brand)}
                            aria-label={`Переименовать бренд ${brand.name}`}
                            title='Переименовать'
                        >
                            <Icon name='edit-2' size='sm' />
                        </Button>
                        <Button
                            type='button'
                            size='sm'
                            emphasis='secondary'
                            variant='ghost'
                            onClick={() => {
                                handleCancelRename()
                                setDeletingBrandId(brand.id)
                            }}
                            aria-label={`Удалить бренд ${brand.name}`}
                            title='Удалить'
                        >
                            <Icon name='trash' size='sm' />
                        </Button>
                    </div>
                )}
            </li>
        )
    }

    return (
        <div className={cn('flex flex-col gap-4', className)}>
            <InputSearch
                value={searchDraft}
                onChange={handleSearchChange}
                placeholder='Поиск бренда'
                size='lg'
                className='w-full'
                aria-label='Поиск бренда'
            />

            <ul className='flex max-h-[280px] min-h-[120px] flex-col gap-1 overflow-y-auto' aria-label='Список брендов'>
                <li
                    className={cn(
                        'group flex items-center gap-2 rounded-[8px] px-3 py-2 transition-colors',
                        selectedBrandId === null ? 'bg-[rgba(255,255,255,0.08)]' : 'hover:bg-[rgba(255,255,255,0.04)]'
                    )}
                >
                    <button
                        type='button'
                        tabIndex={0}
                        className='flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-left'
                        onClick={() => onSelect(null)}
                        onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                onSelect(null)
                            }
                        }}
                        aria-label='Без бренда'
                        aria-pressed={selectedBrandId === null}
                    >
                        <span
                            className={cn(
                                'flex size-4 shrink-0 items-center justify-center rounded-full border',
                                selectedBrandId === null
                                    ? 'border-accent text-accent'
                                    : 'border-[var(--pixorion-gray-2)] text-transparent'
                            )}
                            aria-hidden
                        >
                            <span className={cn('size-2 rounded-full', selectedBrandId === null && 'bg-accent')} />
                        </span>
                        <span className='truncate text-sm text-[var(--pixorion-gray-2)]'>Без бренда</span>
                    </button>
                </li>

                {isLoading && <li className='px-3 py-2 text-sm text-[var(--pixorion-gray-2)]'>Загрузка…</li>}

                {!isLoading && brands.length === 0 && (
                    <li className='px-3 py-2 text-sm text-[var(--pixorion-gray-2)]'>
                        {search ? 'Бренды не найдены' : 'Брендов пока нет'}
                    </li>
                )}

                {brands.map(renderBrandRow)}
            </ul>

            <div className='flex items-center gap-2 border-t border-[rgba(255,255,255,0.08)] pt-4'>
                <Input
                    value={newBrandName}
                    onChange={e => setNewBrandName(e.target.value)}
                    onKeyDown={handleNewBrandKeyDown}
                    placeholder='Название нового бренда'
                    size='lg'
                    emphasis='neutral'
                    className='w-full'
                    aria-label='Название нового бренда'
                />
                <Button
                    type='button'
                    emphasis='accent'
                    size='lg'
                    onClick={handleCreateBrand}
                    disabled={isCreating || !newBrandName.trim()}
                    loading={isCreating}
                    aria-label='Создать бренд'
                    className='shrink-0'
                >
                    <Icon name='plus' size='sm' />
                    Создать
                </Button>
            </div>
        </div>
    )
}
